// src/pages/agenda/Filtros.js — Tipos e Equipe (coluna da esquerda).
//
// A contagem ao lado de cada tipo é do período na tela, sem filtro — é o
// que a pessoa usaria pra decidir o que esconder. "Equipe" só aparece pra
// quem vê a equipe (supervisor); o agente só tem o que é dele.
//
// A engrenagem em Tipos (só supervisor) escolhe como a empresa chama a
// consulta — decisão de out/26: "cada empresa escolhe".
//
// Fase 2: "Lembrete ao cliente" (só supervisor) mostra se o lembrete
// automático está ligado e abre a configuração.
import { useState } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { FiCheck, FiSettings } from 'react-icons/fi';
import { ORDEM_TIPOS, avatar, botaoBase, cartao, cor, rotuloSecao, rotuloTipo, TIPOS, FONT_NUM } from './estilo';

const linha = {
  ...botaoBase, display: 'flex', alignItems: 'center', gap: '10px', p: '5px 4px', border: 'none', borderRadius: '6px',
  bgcolor: 'transparent', textAlign: 'left', width: '100%', '&:hover': { bgcolor: 'var(--ch-inset)' },
  '@media (pointer: coarse)': { minHeight: 40 },
};

function Caixa({ on, base }) {
  return (
    <Box component="span" sx={{
      width: 15, height: 15, boxSizing: 'border-box', flexShrink: 0, borderRadius: '4px',
      border: `1.5px solid ${on ? base : 'var(--ch-bdr2)'}`, bgcolor: on ? base : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ch-card)',
    }}>
      {on && <FiCheck size={11} strokeWidth={3} />}
    </Box>
  );
}

export default function Filtros({
  tipos, onTipo, contagem, nome, equipe, pessoas, onPessoa, veEquipe, opcoesNome, onNome,
  lembrete, onConfigLembrete,
}) {
  const [menu, setMenu] = useState(null);
  const lista = ORDEM_TIPOS.filter((t) => veEquipe || t !== 'disparo');
  return (
    <Box sx={{ ...cartao, flex: 'none', p: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '2px' }}>
        <Box component="span" sx={rotuloSecao}>Tipos</Box>
        {veEquipe && (
          <Tooltip title="Como sua empresa chama os horários marcados com clientes">
            <Box component="button" type="button" aria-label="Escolher o nome da consulta" onClick={(e) => setMenu(e.currentTarget)}
              sx={{ ...botaoBase, display: 'flex', p: '4px', border: 'none', borderRadius: '5px', bgcolor: 'transparent', color: 'var(--ch-text3)',
                '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' }, '@media (pointer: coarse)': { p: '10px' } }}>
              <FiSettings size={13} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {lista.map((t) => {
        const on = tipos[t];
        return (
          <Box key={t} component="button" type="button" role="checkbox" aria-checked={on} onClick={() => onTipo(t)} sx={linha}>
            <Caixa on={on} base={cor(TIPOS[t].c).base} />
            <Box component="span" sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 500, color: 'var(--ch-text)' }}>{rotuloTipo(t, nome)}</Box>
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.72rem', color: 'var(--ch-text3)' }}>{contagem[t] || 0}</Box>
          </Box>
        );
      })}

      {veEquipe && equipe.length > 0 && (
        <>
          <Box component="span" sx={{ ...rotuloSecao, m: '10px 0 2px' }}>Equipe</Box>
          {equipe.map((p) => {
            const on = pessoas[p.id] !== false;
            const av = avatar(p.id);
            return (
              <Box key={p.id} component="button" type="button" role="checkbox" aria-checked={on} onClick={() => onPessoa(p.id)} sx={linha}>
                <Caixa on={on} base="var(--ch-text2)" />
                <Box component="span" sx={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', bgcolor: av.bg, color: av.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700 }}>
                  {p.iniciais}
                </Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: 500, color: 'var(--ch-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.nome}
                </Box>
              </Box>
            );
          })}
        </>
      )}

      {veEquipe && onConfigLembrete && (
        <>
          <Box component="span" sx={{ ...rotuloSecao, m: '10px 0 2px' }}>Lembrete ao cliente</Box>
          <Box component="button" type="button" onClick={onConfigLembrete}
            sx={{ ...linha, alignItems: 'flex-start', flexDirection: 'column', gap: '2px', p: '6px 4px' }}>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--ch-text)' }}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: lembrete?.ativo ? 'var(--ch-green)' : 'var(--ch-bdr2)' }} />
              {lembrete?.ativo ? 'Ligado' : 'Desligado'}
            </Box>
            <Box component="span" sx={{ fontSize: '0.68rem', color: 'var(--ch-text2)', lineHeight: 1.4 }}>
              {lembrete?.ativo ? `${(lembrete.antecedencias || []).join(' · ')} · Configurar` : 'Avise o cliente antes do horário · Configurar'}
            </Box>
          </Box>
        </>
      )}

      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}
        PaperProps={{ sx: { bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', border: '1px solid var(--ch-bdr)', minWidth: 220 } }}>
        <Box sx={{ px: 2, pt: 1, pb: 0.5, fontSize: '0.7rem', color: 'var(--ch-text2)', maxWidth: 240, lineHeight: 1.45 }}>
          Como sua empresa chama os horários marcados com clientes:
        </Box>
        {(opcoesNome || []).map((o) => (
          <MenuItem key={o.chave} selected={o.chave === nome?.chave}
            onClick={() => { setMenu(null); if (o.chave !== nome?.chave) onNome(o.chave); }}
            sx={{ fontSize: '0.82rem', fontFamily: "'Syne', sans-serif", gap: 1, minHeight: { xs: 44, md: 36 } }}>
            <Box component="span" sx={{ width: 14, display: 'flex', color: 'var(--ch-green)' }}>{o.chave === nome?.chave && <FiCheck size={14} />}</Box>
            {o.singular}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
